/**
 * contentTrail — a rolling local record of a list's ACTUAL TEXT, kept only for
 * as long as we're hunting a data-loss bug.
 *
 * WHY (Walter 2026-08-31). He lost two small edits in a few hours — "one was a
 * deletion of a couple of words, the other a couple of words inserted in a
 * sentence" — in a plain, non-reminder list. The sync log records DECISIONS
 * (sizes and fingerprints); the list history keeps DAILY snapshots. Neither
 * can answer the only question that matters: what did the text say a minute
 * before the words disappeared, and what replaced it? His suggestion, and it's
 * the right one: *"maybe for diagnostics, you should just snapshot everything
 * all the time for a little while?"*
 *
 * Deliberate design choices:
 *
 *  - LOCAL ONLY. localStorage, never Firestore. Zero write cost, zero sync
 *    risk, nothing new that can itself corrupt a list. Cost was his explicit
 *    constraint when we built the sync log, and this writes far more often.
 *  - IT HOLDS REAL NOTE TEXT, unlike syncLog. That's the whole point, and it's
 *    why this is TEMPORARY and off by default — see `isTrailEnabled`. He turns
 *    it on, we catch the bug, he turns it off.
 *  - The offline queue lives in the same localStorage and holds unsaved user
 *    content. It must never be evicted to make room for a diagnostic, so the
 *    budget here is small and self-pruning, oldest first.
 *  - Every entry says WHY the text changed (`save`, `adopt`, `merge`,
 *    `conflict`), because "the text changed and nobody typed" is precisely the
 *    shape of the bug.
 */

import { getWindowLabel } from './windowId';

const KEY = 'lists_content_trail_v1';
const FLAG_KEY = 'lists_content_trail_on';
const MAX_ENTRIES = 60;
// ~1.5MB. His biggest list is ~27KB, so this holds a useful depth without
// crowding the offline queue.
const MAX_BYTES = 1500 * 1024;

export function isTrailEnabled() {
  try { return localStorage.getItem(FLAG_KEY) === '1'; } catch { return false; }
}

export function setTrailEnabled(on) {
  try {
    if (on) localStorage.setItem(FLAG_KEY, '1');
    else { localStorage.removeItem(FLAG_KEY); localStorage.removeItem(KEY); }
  } catch { /* private mode */ }
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function write(entries) {
  let out = entries.slice(-MAX_ENTRIES);
  try {
    // Prune oldest until we're inside budget. Never let a diagnostic grow
    // until it evicts the offline queue.
    while (out.length > 1 && JSON.stringify(out).length > MAX_BYTES) out = out.slice(1);
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch { /* quota or private mode — a diagnostic is never worth throwing */ }
}

/**
 * recordContent(listId, content, reason) — append a version, unless it's
 * identical to the last one recorded for that list (typing produces a save
 * every few seconds; only CHANGES are interesting).
 */
export function recordContent(listId, content, reason) {
  if (!isTrailEnabled()) return;
  try {
    const text = String(content ?? '');
    const entries = read();
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].list === listId) {
        if (entries[i].text === text) return;   // nothing changed
        break;
      }
    }
    // `w` is the WINDOW, not the device (lib/windowId). Walter runs the PWA
    // and a Chrome window side by side; they share an origin and therefore a
    // clientId, so without this every entry looks like one writer and a
    // two-window fight is invisible.
    entries.push({ t: Date.now(), list: listId, reason: String(reason || ''), w: getWindowLabel(), text });
    write(entries);
  } catch { /* ignore */ }
}

export function readContentTrail() {
  return read();
}

/**
 * A paste-ready export — a DIFF, not a dump of every version.
 *
 * Walter 2026-09-03, on the first real export: *"I think the content trail I
 * just dropped in was too big (100k lines), and everything crashed."* My
 * mistake: the byte cap bounded what we STORE, but this printed the full text
 * of all 50 versions, and his list is 30k characters — so the export ran to
 * ~1.5MB of near-identical prose with the six interesting lines buried in it.
 *
 * Now each version prints only the lines that CHANGED against the previous
 * version of that same list, which for a normal edit is a handful. The first
 * version of a list prints a short head as an anchor, and a version with no
 * textual change collapses to one line. The full text is still in
 * localStorage if a specific version is ever needed.
 */
export function formatContentTrail() {
  const entries = read();
  if (!entries.length) return 'content trail is empty (turn it on in Settings, then reproduce the problem)';
  const prevByList = new Map();
  const out = [
    `listicity content trail (${entries.length} versions, oldest first) — CHANGED LINES only`,
    '',
  ];
  for (const e of entries) {
    const when = new Date(e.t).toISOString().replace('T', ' ').slice(0, 19);
    const prev = prevByList.get(e.list);
    const lines = e.text.split('\n').length;
    const head = `──── ${when}  list=${e.list}  ${e.reason}  w=${e.w || '?'}  ${e.text.length} chars, ${lines} lines`;
    if (prev == null) {
      out.push(`${head}  (first version — first 8 lines)`);
      out.push(...e.text.split('\n').slice(0, 8).map(l => `  ${l}`));
    } else {
      const { added, removed } = diffChangedLines(prev, e.text);
      if (added.length === 0 && removed.length === 0) {
        out.push(`${head}  (no line-level change)`);
      } else {
        out.push(`${head}  · +${added.length} / -${removed.length} lines`);
        out.push(...removed.map(l => `  - ${l}`));
        out.push(...added.map(l => `  + ${l}`));
      }
    }
    prevByList.set(e.list, e.text);
    out.push('');
  }
  return out.join('\n');
}

/**
 * Lines present in one version and not the other, counting duplicates so a
 * line appearing twice and cut once shows as a single removal. Blank lines are
 * ignored — his lists are full of them and they'd drown the signal. Same
 * consume-from-a-pool approach as lib/lineDiff, kept local so a diagnostic
 * never depends on rendering code.
 */
function diffChangedLines(beforeText, afterText) {
  const before = String(beforeText ?? '').split('\n');
  const after = String(afterText ?? '').split('\n');
  const afterPool = new Map();
  for (const l of after) afterPool.set(l, (afterPool.get(l) || 0) + 1);
  const removed = [];
  for (const l of before) {
    const c = afterPool.get(l) || 0;
    if (c > 0) afterPool.set(l, c - 1);
    else if (l.trim() !== '') removed.push(l);
  }
  const beforePool = new Map();
  for (const l of before) beforePool.set(l, (beforePool.get(l) || 0) + 1);
  const added = [];
  for (const l of after) {
    const c = beforePool.get(l) || 0;
    if (c > 0) beforePool.set(l, c - 1);
    else if (l.trim() !== '') added.push(l);
  }
  return { added, removed };
}
